import axios from 'axios'
import menuData from '@/mockdata/menu-data.json'
import VideoUrlManager from '@/utils/videoUrlManager'
import BridgeEventBus from '@/utils/BridgeEventBus'

// API基础URL，实际项目中应该从环境变量或配置文件中获取
const API_URL = process.env.VUE_APP_API_URL || ''

/**
 * 处理菜单数据，动态更新视频监控URL
 * @param {Array} menus 菜单数据
 * @returns {Promise<Array>} 处理后的菜单数据
 */
async function processMenuData(menus) {
  // 克隆菜单数据，避免修改原始对象
  const processedMenus = JSON.parse(JSON.stringify(menus));
  
  try {
    // 从VideoUrlManager获取URL
    const videoUrl = await VideoUrlManager.getVideoUrl();
    
    // 更新视频监控菜单的URL
    const videoMenu = processedMenus.find(item => item.id === 'videoMonitor');
    if (videoMenu) {
      videoMenu.externalUrl = videoUrl;
      console.log('menu.js: 已更新视频监控URL:', videoUrl);
      
      // 同时监听视频URL的更新
      BridgeEventBus.$once('videoUrl-updated', ({ videoUrl: newUrl }) => {
        if (newUrl && newUrl !== videoUrl) {
          console.log('menu.js: 检测到视频URL更新，将在下次菜单加载时使用新URL');
        }
      });
    }
  } catch (error) {
    console.error('获取视频监控URL失败:', error);
  }
  
  return processedMenus;
}

/**
 * 菜单API服务
 * 提供菜单数据的CRUD操作
 */
export default {
  /**
   * 获取菜单数据
   * @param {boolean} [useMock=true] 是否使用模拟数据
   * @returns {Promise<Array<{
   *   id: number,
   *   name: string,
   *   path: string,
   *   component: string,
   *   redirect: string,
   *   meta: {
   *     title: string,
   *     icon: string,
   *     hidden: boolean,
   *     roles: Array<string>,
   *     keepAlive: boolean
   *   },
   *   children: Array<Object>
   * }>>} 菜单数据Promise
   */
  async getMenuData(useMock = true) {
    // 开发环境或强制使用模拟数据时，返回本地数据
    if (useMock || process.env.NODE_ENV === 'development') {
      // 模拟网络延迟
      return new Promise((resolve) => {
        setTimeout(async () => {
          // 处理菜单数据，更新视频监控URL
          const processedMenus = await processMenuData(menuData.menus);
          resolve(processedMenus);
        }, 200);
      });
    }
    
    // 生产环境从服务器获取菜单数据
    try {
      const response = await axios.get(`${API_URL}/api/menus`);
      const menus = response.data.data || [];
      // 处理菜单数据，更新视频监控URL
      return await processMenuData(menus);
    } catch (error) {
      console.error('获取菜单数据失败:', error);
      // 发生错误时返回处理后的本地数据作为后备
      return await processMenuData(menuData.menus);
    }
  },
  
  /**
   * 根据用户权限获取菜单数据
   * @param {string|number} userId 用户ID
   * @param {boolean} [useMock=true] 是否使用模拟数据
   * @returns {Promise<Array<{
   *   id: number,
   *   name: string,
   *   path: string,
   *   component: string,
   *   redirect: string,
   *   meta: {
   *     title: string,
   *     icon: string,
   *     hidden: boolean,
   *     roles: Array<string>,
   *     keepAlive: boolean
   *   },
   *   children: Array<Object>
   * }>>} 菜单数据Promise
   */
  async getMenuByUserId(userId, useMock = true) {
    // 开发环境或强制使用模拟数据时，返回本地数据
    if (useMock || process.env.NODE_ENV === 'development') {
      // 模拟网络延迟
      return new Promise((resolve) => {
        setTimeout(async () => {
          // 这里可以添加基于用户ID进行权限筛选的逻辑
          // 在实际应用中，可以根据用户角色过滤菜单
          // 处理菜单数据，更新视频监控URL
          const processedMenus = await processMenuData(menuData.menus);
          resolve(processedMenus);
        }, 200);
      });
    }
    
    // 生产环境从服务器获取用户特定菜单数据
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}/menus`);
      const menus = response.data.data || [];
      // 处理菜单数据，更新视频监控URL
      return await processMenuData(menus);
    } catch (error) {
      console.error('获取用户菜单数据失败:', error);
      // 处理菜单数据，更新视频监控URL
      return await processMenuData(menuData.menus);
    }
  },
  
  /**
   * 检查当前路由是否匹配菜单项
   * @param {Object} menu 菜单项
   * @param {string} menu.path 菜单路径
   * @param {string} [menu.activeMatch] 匹配规则，多个规则用逗号分隔
   * @param {boolean} [menu.activeStartsWith] 是否使用startsWith进行匹配
   * @param {string} path 当前路由路径
   * @returns {boolean} 是否匹配
   */
  isMenuActive(menu, path) {
    if (!menu || !path) return false
    
    // 直接路径匹配
    if (menu.path === path) return true
    
    // 通过activeMatch属性匹配
    if (menu.activeMatch) {
      if (menu.activeStartsWith) {
        // 如果设置了activeStartsWith，检查路径是否以activeMatch开头
        return path.startsWith(menu.activeMatch)
      } else {
        // 否则，检查路径是否在activeMatch列表中
        const matches = menu.activeMatch.split(',')
        return matches.some(match => path === match.trim())
      }
    }
    
    return false
  }
} 